import { Edit, Lock, LockOpen } from "@mui/icons-material";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ClearIcon from "@mui/icons-material/Clear";
import EmailIcon from "@mui/icons-material/Email";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce, isEmpty, isNil } from "lodash";
import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useApi, useApiMutation } from "../hooks/useApi";
import { Client } from "../types/client";
import { Profile, SystemUser } from "../types/contact";
import PromoteDialog from "./PromoteDialog";
import UnlockDialog from "./UnlockDialog";

interface Props { }

const MobileAdminData: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const { data: profile } = useApi<Profile>("profile");
  const [filteredClients, setFilteredClients] = useState<Client[] | undefined>(
    profile?.results?.clients?.filter((client) => client.isPrimary)
  );
  const [page, setPage] = useState<number>(0);
  const [unlockOpen, setUnlockOpen] = useState<boolean>(false);
  const [promoteOpen, setPromoteOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SystemUser | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchRef = useRef<HTMLInputElement>();
  const [promoted, setPromoted] = useState<boolean>(false);
  const { data: systemusersQuery, isLoading } = useApi<SystemUser[]>(
    "systemusers",
    { clientId: selectedClientId, search: searchQuery },
    {
      count: 5,
      page: page + 1,
    },
    !isNil(selectedClientId)
  );

  const count = systemusersQuery?.paging?.totalCount ?? 0;
  const pages = systemusersQuery?.paging?.totalPages ?? 0;

  const { apiUpdate } = useApiMutation();

  const { t } = useTranslation();

  const handleUnlockOpen = () => {
    setUnlockOpen(true);
  };
  const handleUnlockClose = () => {
    setUnlockOpen(false);
    setSelectedItem(null)
  };

  const handleChangePage = (
    event: ChangeEvent<unknown> | null,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handlePromoteOpen = () => {
    setPromoteOpen(true);
    // setUnlockOpen(false)
  };
  const handlePromoteClose = () => {
    setPromoteOpen(false);
  };

  const handleUnlockClick = (selectedItem: SystemUser | null) => {
    apiUpdate.mutate(
      {
        entity: "systemusers",
        post: {
          id: selectedItem?.uid,
          isLocked: false,
          uid: selectedItem?.uid,
        },
      },
      {
        onError: (error) => console.error(error),
      }
    );
  };

  const handleLockClick = (selectedItem: SystemUser | null) => {
    apiUpdate.mutate(
      {
        entity: "systemusers",
        post: {
          id: selectedItem?.uid,
          isLocked: true,
          uid: selectedItem?.uid,
        },
      },
      {
        onError: (error) => console.error(error),
      }
    );
  };

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedClientId(event.target.value);
    setPage(0);
  };

  useEffect(() => {
    if (profile?.results?.clients && profile.results.clients.length > 0) {
      const filteredClients = profile?.results?.clients?.filter(
        (client) => client.isPrimary
      );
      setFilteredClients(filteredClients);
      if (filteredClients) {
        setSelectedClientId(filteredClients[0]?.id || "");
      }
    }
  }, [profile]);

  return (
    <>
      <Stack
        direction={"column"}
        justifyContent={"center"}
        alignItems={"center"}
        spacing={3}
        mb={3}
      >
        {filteredClients && filteredClients.length > 1 ? (
          selectedClientId && (
            <FormControl sx={{ width: "100%" }}>
              <InputLabel id="demo-simple-select-label" size="small">
                {" "}
                {t("adminPage.client")}{" "}
              </InputLabel>
              <Select
                size="small"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedClientId || ""}
                label={t("adminPage.client")}
                onChange={handleChange}
              >
                {filteredClients.map((client) => (
                  <MenuItem key={`clientList-${client.id}`} value={client.id}>
                    {" "}
                    {client.name}{" "}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        ) : (
          <Typography variant="h6" sx={{ m: "10px 0 10px 0" }}>
            {" "}
            {filteredClients && filteredClients[0].name}{" "}
          </Typography>
        )}

        <FormControl variant="outlined" sx={{ width: "100%" }}>
          <InputLabel size="small">{t("adminPage.search")}</InputLabel>
          <OutlinedInput
            inputRef={searchRef}
            size="small"
            onChange={debounce((event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchQuery(event.target.value);
            }, 1000)}
            type="text"
            endAdornment={
              !isEmpty(searchQuery) && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setSearchQuery("");
                      searchRef.current!.value = "";
                    }}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
            label={t("adminPage.search")}
          />
        </FormControl>
      </Stack>

      {!isLoading ? (
        <>
          {systemusersQuery?.results &&
            systemusersQuery?.results?.length > 0 ? (
            <>
              {systemusersQuery?.results?.map((item, index) => (
                <Accordion key={`systemuser-${item.uid}`}>
                  <AccordionSummary
                    // expandIcon={<ArrowDownward />}
                    expandIcon={<KeyboardArrowDownIcon />}
                  >
                    <Stack
                      direction={"row"}
                      alignContent={"center"}
                      spacing={2}
                    >
                      {item.isPrimary && (
                        <Tooltip title={t("tooltips.primaryContact")} arrow>
                          <StarsRoundedIcon
                            fontSize="small"
                            sx={{ color: "#005292" }}
                          />
                        </Tooltip>
                      )}
                      <Typography variant="body2">{item.name}</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "#F6F6F6" }}>
                    <Stack
                      direction={"column"}
                      padding={1}
                      spacing={2}
                      sx={{ justifyContent: "space-between", mt: "20px" }}
                    >
                      <Stack
                        direction={"row"}
                        padding={0}
                        spacing={2}
                        width={"100%"}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <EmailIcon fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography variant="body2">
                            {" "}
                            {item.email}{" "}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={"rgba(0, 0, 0, 0.6)"}
                          >
                            {" "}
                            {t("registration.email")}{" "}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction={"row"}
                        padding={0}
                        spacing={2}
                        width={"100%"}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          {item.isLocked ? (
                            <Lock fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
                          ) : (
                            <LockOpen fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "start",
                          }}
                        >
                          {item.isLocked === false ? (
                            <Chip
                              label={t("adminTable.active")}
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label={t("adminTable.locked")}
                              color="error"
                              size="small"
                            />
                          )}
                          <Typography
                            variant="body2"
                            color={"rgba(0, 0, 0, 0.6)"}
                          >
                            {" "}
                            {t("adminTable.status")}{" "}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction={"row"}
                        padding={0}
                        spacing={2}
                        width={"100%"}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <AccessTimeFilledIcon fontSize="large" sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "start",
                            alignItems: "start",
                          }}
                        >
                          {item.accessLevel === 1 ? (
                            <Chip
                              label={t("adminTable.own")}
                              color="default"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label={t("adminTable.all")}
                              color="warning"
                              size="small"
                            />
                          )}
                          <Typography
                            variant="body2"
                            color={"rgba(0, 0, 0, 0.6)"}
                          >
                            {t("adminTable.timesheetAccess")}{" "}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    <Stack
                      direction={"column"}
                      spacing={2}
                      alignItems={"center"}
                      paddingTop={"30px"}
                      paddingBottom={"10px"}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ width: "fit-content", height: "30.08px" }}
                        onClick={() =>
                          item.isLocked
                            ? handleUnlockClick(item)
                            : handleLockClick(item)
                        }
                        startIcon={
                          item.isLocked ? (
                            <LockOpen
                              fontSize="small"
                              sx={{ color: "white" }}
                            />
                          ) : (
                            <Lock fontSize="small" sx={{ color: "white" }} />
                          )
                        }
                      >
                        {item.isLocked
                          ? t("adminTable.unlock")
                          : t("adminTable.lock")}
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ width: "fit-content", height: "30.08px" }}
                        onClick={() => {
                          handleUnlockOpen();
                          setSelectedItem(item);
                        }}
                        startIcon={
                          <Edit fontSize="small" sx={{ color: "#005292" }} />
                        }
                      >
                        {t("adminTable.edit")}
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          ) : (
            <Typography sx={{ m: "10px 0 10px 0" }}>
              {" "}
              {t("adminTable.noResults")}{" "}
            </Typography>
          )}
        </>
      ) : (
        <Box paddingTop={4} display="flex" justifyContent="center">
          <CircularProgress size={"50px"} />
        </Box>
      )}

      {systemusersQuery?.results && systemusersQuery.results.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: "20px" }}>
          <Pagination
            count={pages ?? 0}
            color="primary"
            page={count <= 0 ? 0 : page + 1}
            onChange={handleChangePage}
            size="small"
          />
        </Box>
      )}

      <UnlockDialog
        handleUnlockOpen={handleUnlockOpen}
        handleUnlockClose={handleUnlockClose}
        handlePromoteOpen={handlePromoteOpen}
        unlockOpen={unlockOpen}
        setUnlockOpen={setUnlockOpen}
        selectedItem={selectedItem}
        promoted={promoted}
        setPromoted={setPromoted}
      />

      <PromoteDialog
        handlePromoteOpen={handlePromoteOpen}
        handlePromoteClose={handlePromoteClose}
        promoteOpen={promoteOpen}
        setPromoteOpen={setPromoteOpen}
        selectedItem={selectedItem}
        setPromoted={setPromoted}
      />
    </>
  );
};

export default MobileAdminData;
